/**
 * Central conversion tracking helper.
 *
 * Fires:
 *  - GA4 event
 *  - Google Ads conversion (if AW_CONVERSION_ID + label configured)
 *  - GTM dataLayer event
 *  - Meta Pixel event (if pixel loaded)
 *
 * Replace the placeholder AW-XXXXXXXXXX / labels in index.html and here
 * once the user creates them in Google Ads → Tools → Conversions.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
  }
}

// TODO: Replace placeholders with real IDs from Google Ads once account is set up.
export const GOOGLE_ADS_ID = "AW-601414671";
export const CONVERSION_LABELS = {
  lead: "BAPbCKSAvsscEI-4454C", // "Submit lead form" Google Ads conversion label
  phone: "pz60CJr0v8scEI-4454C",
};

function isConfigured(label: string) {
  return !!label && label !== "LEAD_LABEL" && label !== "PHONE_LABEL";
}

interface LeadPayload {
  source: string;       // e.g. "contact-form", "grout-quiz", "free-assessment"
  value?: number;       // Only a verified value for this outcome, never the consultation fee by default
  email?: string;
  phone?: string;
}

export function trackLead(payload: LeadPayload) {
  const value = payload.value;
  const valueFields = value === undefined ? {} : { value, currency: "USD" };

  // GTM / dataLayer - always fires so users can build triggers in GTM.
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "lead_submit",
    lead_source: payload.source,
    ...(value === undefined ? {} : { lead_value: value }),
    page_path: typeof location !== "undefined" ? location.pathname : "",
  });

  // GA4 event
  window.gtag?.("event", "generate_lead", {
    ...valueFields,
    lead_source: payload.source,
  });

  // Google Ads conversion
  if (isConfigured(CONVERSION_LABELS.lead)) {
    window.gtag?.("event", "conversion", {
      send_to: `${GOOGLE_ADS_ID}/${CONVERSION_LABELS.lead}`,
      ...valueFields,
    });
  }

  // Meta Pixel
  window.fbq?.("track", "Lead", { ...valueFields, source: payload.source });
}

// Phone taps are reported once by the site-wide tel: listener in index.html
// (GA4 + Google Ads "Click to call" + Meta Contact). Kept as a no-op so
// callers don't send a second, duplicate conversion for the same tap.
export function trackPhoneConversion(_phoneNumber: string) {
  /* intentionally empty: see index.html tel: listener */
}
